import { format } from 'date-fns';
import { FunctionComponent, MouseEventHandler, memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DATE_TIME_FORMAT } from '../../constants';
import { getCssVar } from '../../utils';
import { IconLabel } from '../IconLabel';
import { InputField } from '../InputField';
import { Modal } from '../Modal';
import { ScheduleIcon } from '../ScheduleIcon';
import { SchedulePicker } from './SchedulePicker';

import classes from './ScheduleInput.module.scss';

export enum ScheduleInputVariant {
  ICON = 'ICON',
  INPUT = 'INPUT',
}

interface ScheduleInputProps {
  time: Date | null;
  setTime: (value: Date | null) => void;
  minDate?: Date | null;
  maxDate?: Date | null;
  title?: string;
  subTitle?: string;
  iconLabel?: string;
  iconHint?: string;
  inputLabel?: string;
  summaryLabel?: string;
  confirmLabel?: string;
  className?: string;
  disabled?: boolean;
  variant?: ScheduleInputVariant;
}

export const ScheduleInput: FunctionComponent<ScheduleInputProps> = memo(
  ({
    time,
    setTime,
    minDate,
    maxDate,
    title,
    subTitle,
    iconLabel,
    iconHint,
    inputLabel,
    summaryLabel,
    confirmLabel,
    className,
    disabled,
    variant = ScheduleInputVariant.ICON,
  }) => {
    const { t } = useTranslation();

    const [isOpen, setIsOpen] = useState(false);

    const open = useCallback(() => {
      if (disabled) {
        return;
      }

      setIsOpen(true);
    }, [disabled]);

    const close = useCallback(() => setIsOpen(false), []);

    const confirm = useCallback(
      (value: Date) => {
        setTime(value);
        setIsOpen(false);
      },
      [setTime]
    );

    const clear = useCallback(() => {
      setTime(null);
      setIsOpen(false);
    }, [setTime]);

    const clearFromTrigger = useCallback<MouseEventHandler>(
      (event) => {
        event.stopPropagation();
        setTime(null);
      },
      [setTime]
    );

    const displayValue = useMemo(() => {
      if (!time) {
        return '';
      }

      return format(time, DATE_TIME_FORMAT);
    }, [time]);

    const renderTrigger = useMemo(() => {
      if (variant === ScheduleInputVariant.INPUT) {
        return (
          <InputField
            value={displayValue}
            label={inputLabel}
            placeholder={t('common.select-date-time')}
            onClick={open}
            className={className}
            disabled={disabled}
            {...(displayValue && {
              icon: (
                <IconLabel
                  iconId={'close'}
                  iconSize={18}
                  ariaLabel={t('common.clear')}
                  color={getCssVar('--input-icon-color')}
                  onClick={clearFromTrigger}
                  singleColor
                />
              ),
            })}
            readOnly
          />
        );
      }

      return (
        <ScheduleIcon
          time={time}
          label={iconLabel}
          hint={iconHint}
          className={className}
          disabled={disabled}
          onClick={open}
        />
      );
    }, [
      variant,
      displayValue,
      inputLabel,
      t,
      open,
      className,
      disabled,
      clearFromTrigger,
      time,
      iconLabel,
      iconHint,
    ]);

    const modalTitle = title ?? inputLabel ?? t('common.select-date-time');

    return (
      <>
        {renderTrigger}

        <Modal
          isOpen={isOpen}
          onClose={close}
          root={'modal-datepicker-root'}
          contentStyle={{
            width: 'fit-content',
            maxWidth: 'calc(100vw - 3rem)',
            boxSizing: 'border-box',
          }}
          ariaLabel={modalTitle}
          body={
            <>
              <div className={classes['schedule-input__title']}>{modalTitle}</div>

              <SchedulePicker
                time={time}
                onConfirm={confirm}
                onClear={clear}
                subTitle={subTitle}
                summaryLabel={summaryLabel}
                confirmLabel={confirmLabel}
                minDate={minDate}
                maxDate={maxDate}
              />
            </>
          }
        />
      </>
    );
  }
);
